import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { MessageCircle, Check, X, ExternalLink, Loader2, Bell, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending:   { label: "Në Pritje",  cls: "bg-[#D4A24C]/15 text-[#8B6914] border-[#D4A24C]/40" },
  sent:      { label: "Dërguar",    cls: "bg-[#2C4C3B] text-[#FDFBF7] border-[#2C4C3B]" },
  failed:    { label: "Dështoi",    cls: "bg-[#9E3C3C]/15 text-[#9E3C3C] border-[#9E3C3C]/40" },
  dismissed: { label: "I Anuluar",  cls: "bg-[#EAE4D9] text-[#5C5C5C] border-[#C9C2B2]" },
};

const FILTERS = [
  { key: "pending",   label: "Në Pritje" },
  { key: "sent",      label: "Dërguar"   },
  { key: "dismissed", label: "Anuluar"   },
  { key: "all",       label: "Të Gjitha" },
];

const fmt = (iso) => iso ? new Date(iso).toLocaleString("sq-AL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/notifications/whatsapp", { params });
      setItems(data);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const send = async (it) => {
    window.open(it.wa_url, "_blank", "noopener,noreferrer");
    try {
      await api.post(`/notifications/whatsapp/${it.id}/mark-sent`);
      toast.success("Lajmërimi u shënua si i dërguar");
      load();
    } catch { toast.error("Gabim"); }
  };

  const dismiss = async (id) => {
    try {
      await api.post(`/notifications/whatsapp/${id}/dismiss`);
      load();
    } catch { toast.error("Gabim"); }
  };

  return (
    <div className="space-y-8" data-testid="notifications-page">
      <div>
        <div className="label-eyebrow">Komunikim</div>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Lajmërime WhatsApp</h1>
        <p className="mt-2 text-sm text-[#5C5C5C]">
          Çdo takim i ri krijon automatikisht një lajmërim WhatsApp për doktorin. Klikoni "Dërgo" për ta dërguar me 1 klikim.
        </p>
      </div>

      <div className="flex gap-1 flex-wrap" data-testid="notif-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)}
                  data-testid={`filter-${f.key}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    filter === f.key
                      ? "bg-[#2C4C3B] text-[#FDFBF7]"
                      : "bg-white border border-[#E2DFD8] text-[#5C5C5C] hover:text-[#2C4C3B]"
                  }`}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#5C5C5C]"><Loader2 className="inline animate-spin" size={16} /> Po ngarkohen…</div>
      ) : items.length === 0 ? (
        <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]">
          <Bell size={32} className="mx-auto mb-3 text-[#E2DFD8]" />
          Nuk ka lajmërime në këtë kategori.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.pending;
            return (
              <li key={it.id} className="surface-card p-4" data-testid={`notif-${it.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                      <MessageCircle size={14} className="text-[#25D366] shrink-0" />
                      Te: <b>{it.doctor_name}</b>
                      <span className="text-xs text-[#5C5C5C] font-mono">({it.to_phone})</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium border ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <div className="text-xs text-[#5C5C5C] mt-1">
                      Krijuar: {fmt(it.created_at)}
                      {it.sent_at && <> · Dërguar: {fmt(it.sent_at)}</>}
                    </div>
                  </div>
                </div>
                <pre className="text-xs whitespace-pre-wrap text-[#1A1A1A] bg-[#FDFBF7] p-3 rounded border border-[#E2DFD8] max-h-40 overflow-y-auto">{it.text}</pre>
                {it.status === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => send(it)}
                            className="inline-flex items-center gap-2 rounded-md bg-[#25D366] text-white px-3 py-1.5 text-xs font-medium hover:bg-[#1ebe5b]"
                            data-testid={`send-${it.id}`}>
                      <ExternalLink size={12} /> Dërgo në WhatsApp
                    </button>
                    <button onClick={() => dismiss(it.id)}
                            className="inline-flex items-center gap-1 rounded-md border border-[#E2DFD8] text-[#5C5C5C] px-3 py-1.5 text-xs hover:text-[#9E3C3C] hover:border-[#9E3C3C]"
                            data-testid={`dismiss-${it.id}`}>
                      <X size={12} /> Anulo
                    </button>
                  </div>
                )}
                {it.status === "sent" && it.wa_url && (
                  <a href={it.wa_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 mt-3 text-xs text-[#2C4C3B] hover:underline">
                    <ExternalLink size={10} /> Ridërgo (nëse nevojitet)
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
